import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TranslationProvider from '../../../providers/TranslationProvider';
import { SmsForm } from '../../Form';
import { postAuthMobile } from '../../../api/api';
import { Logger } from '../../../utils';
import {
  getPhoneValidationError,
} from '../../../utils/phoneValidation';

const UpdatePhone = (props) => {
  const { countryCodes, callingCode, smsTriggered } = props;

  const [showCaptcha, setShowCaptcha] = useState();
  const [countryCode, setCountryCode] = useState();
  const [error, setError] = useState({});
  const [phoneValidationError, setPhoneValidationError] = useState(null);
  const [disableContinue, setDisableContinue] = useState(false);
  const [hasEditedPhoneSinceSubmit, setHasEditedPhoneSinceSubmit] = useState(false);

  const getGrecaptcha = () => {
    if (window.grecaptcha && window.grecaptcha.render) {
      return window.grecaptcha;
    }
    return undefined;
  };

  const renderCaptcha = () => {
    const grecaptcha = getGrecaptcha();
    if (!grecaptcha) {
      setTimeout(renderCaptcha, 500);
      return;
    }
    grecaptcha.render('captcha__container', {
      sitekey: '6Lfw-dIZAAAAAESRBH4JwdgfTXj5LlS1ewlvvCYe',
    });
  };

  const requiresCaptcha = (code) => {
    const country = countryCodes?.find(
      (val) => val.code === code
    );
    return !!(country && country.captcha);
  };

  useEffect(() => {
    if (showCaptcha) {
      renderCaptcha();
    }
  }, [showCaptcha]);

  useEffect(() => {
    setShowCaptcha(requiresCaptcha(countryCode));
  }, [countryCode]);

  const onChange = (data) => {
    setCountryCode(data.country_code);
    const nextPhoneValidationError = getPhoneValidationError(
      data,
      { fieldName: 'newPhone' }
    );
    setPhoneValidationError(nextPhoneValidationError);
    setDisableContinue(!!nextPhoneValidationError);
    setHasEditedPhoneSinceSubmit(true);
  };

  const onSubmit = async (data) => {
    setHasEditedPhoneSinceSubmit(false);
    setError({});
    const phoneError = getPhoneValidationError(
      data,
      { validateIncomplete: true, fieldName: 'newPhone' }
    );
    if (phoneError) {
      setPhoneValidationError(phoneError);
      setDisableContinue(true);
      throw { data: { mobile_number: phoneError } }; // eslint-disable-line no-throw-literal
    }

    setPhoneValidationError(null);
    setDisableContinue(false);
    try {
      const submittedCountryCode = data.country_code;
      const normalizedPhoneDigits = (data.newPhone || '').replace(/\D/g, '');
      const response = await postAuthMobile({
        mobile_number: `+${submittedCountryCode}${normalizedPhoneDigits}`,
        device_type_id: 3,
        method: 'sms',
        captcha_token: data['g-recaptcha-response'],
      });
      if (response?.sent) {
        smsTriggered(`+${submittedCountryCode}${normalizedPhoneDigits}`);
      } else {
        setError(response?.data);
        if (response?.data?.mobile_number) {
          setDisableContinue(true);
        }
        throw response;
      }
    } catch (err) {
      const serverPhoneError = err?.data?.data?.mobile_number || err?.data?.mobile_number;
      if (serverPhoneError) {
        setDisableContinue(true);
      }
      Logger.error('[UpdatePhoneNumber] Error:', err);
      throw err;
    }
  };

  return (
    <>
      <TranslationProvider
        translationKey="mobile_number_new"
      >
        <h1 className="UpdatePhoneView__Headline" style={{ marginBottom: '30px' }}>
          Enter your new mobile number
        </h1>
      </TranslationProvider>
      <SmsForm
        onFormValueChange={onChange}
        onSubmit={onSubmit}
        countryCodes={countryCodes}
        showCaptcha={showCaptcha}
        error={error}
        name="newPhone"
        defaultCountryCode={callingCode}
        clientPhoneError={phoneValidationError}
        disableSubmit={disableContinue}
        suppressServerPhoneError={hasEditedPhoneSinceSubmit}
      />
      <TranslationProvider translationKey="mobile_number_desc">
        <div className="UpdatePhoneView__Footnote">
          By providing your mobile number, you agree that it may be used to send you text messages and call you to confirm and manage your reservations.
        </div>
      </TranslationProvider>
    </>
  );
};

UpdatePhone.propTypes = {
  smsTriggered: PropTypes.func.isRequired,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({})),
  callingCode: PropTypes.string,
};

UpdatePhone.defaultProps = {
  countryCodes: undefined,
  callingCode: undefined,
};

const mapStateToProps = ({ ipdata, resyConfig }) => {
  const { countryCode } = ipdata;
  const { config } = resyConfig;
  let callingCode = null;
  const countryCodes = config?.country_codes;
  if (config && countryCode) {
    const { country_codes: countries } = config;
    const country = countries.find(
      (value) => (countryCode === value.iso3166)
    );
    callingCode = country.code;
  }
  return { callingCode, countryCodes };
};

export { UpdatePhone as ComponentUnderTest, mapStateToProps };

export default connect(mapStateToProps)(UpdatePhone);
